import type { IGetTextPrice } from '~/modules/shopify';
import type { AnalyticValueType } from '../types';

import {
  numberWithCommas,
  getTimeDurationLabel,
  getFormattedByInterval,
  AnalyticInterval,
  calcPercentageString,
} from '~/utils';
import { EAnalyticColumnKey, EAnalyticDataType } from '../types';
import { formatAnalyticDate } from './formatAnalyticDate';
import { NONE_VALUE } from '../constants';

export const formatAnalyticData = ({
  value,
  formatter,
  getTextPrice,
  name,
}: {
  value: AnalyticValueType;
  formatter?: string;
  getTextPrice: IGetTextPrice;
  name?: string;
}): AnalyticValueType => {
  const dataTypeIsObjectOrArray =
    value !== null && (typeof value === 'object' || Array.isArray(value));

  if (dataTypeIsObjectOrArray) return value;

  switch (formatter) {
    case EAnalyticDataType.INTEGER: {
      return numberWithCommas((value ?? 0).toString());
    }

    case EAnalyticDataType.CURRENCY: {
      return getTextPrice(value, false);
    }

    case EAnalyticDataType.DATE: {
      return formatAnalyticDate(value);
    }

    case EAnalyticDataType.PERCENT: {
      if (typeof value !== 'number') return calcPercentageString(0, 1, 2) ?? '';
      return calcPercentageString(value / 100, 1, 2) ?? '';
    }

    case EAnalyticDataType.DURATION: {
      return getTimeDurationLabel(Number(value));
    }

    case EAnalyticDataType.STRING: {
      const fallbackValue = name === EAnalyticColumnKey.CAMPAIGNS ? '' : NONE_VALUE;

      return value ?? fallbackValue;
    }

    case EAnalyticDataType.DAY: {
      return getFormattedByInterval(value, AnalyticInterval.DAY, {
        isExpandDetail: true,
      });
    }

    case EAnalyticDataType.HOUR: {
      return getFormattedByInterval(value, AnalyticInterval.HOUR, {
        isExpandDetail: true,
      });
    }

    case EAnalyticDataType.MONTH: {
      return getFormattedByInterval(value, AnalyticInterval.MONTH, {
        isExpandDetail: true,
      });
    }

    case EAnalyticDataType.YEAR: {
      return getFormattedByInterval(value, AnalyticInterval.YEAR);
    }

    case EAnalyticDataType.WEEK: {
      return getFormattedByInterval(value, AnalyticInterval.WEEK, {
        isExpandDetail: true,
      });
    }

    case EAnalyticDataType.QUARTER: {
      return getFormattedByInterval(value, AnalyticInterval.QUARTER, {
        isExpandDetail: true,
      });
    }

    case EAnalyticDataType.OBJECT:
    case EAnalyticDataType.ARRAY: {
      return value;
    }

    default:
      return `${value}`;
  }
};
